import GenericModal from "@irati/ui/src/GenericModal"
import React, {useContext, useMemo, createContext, useState, ComponentProps} from "react"
import {useBudgetsPermissions} from "@irati/wallet/hooks/budget"
import tracking from "@irati-tracking/wallet/rewardPointsWallet"
import IntlMarkdown from "../../components/styled/IntlMarkdown"
import {useBudgetManagement} from "./hooks"
import {H1} from "@irati/ui/src/Typography"
import FormattedMessage from "@irati/utils/lang"
import styled, {css} from "styled-components"

const List = styled.ul`
  ${({theme}) => css`
    margin-bottom: ${theme.spacing.four};

    & {
      padding-inline-start: ${theme.spacing.two};
    }

    & li {
      padding-left: ${theme.spacing.one};
      padding-top: ${theme.spacing.four};
    }
  `};
`

interface Context {
  showConfirm(): void
  showCancel(): void
}

const modalContext = createContext<Context | null>(null)

export function useBudgetModals() {
  const context = useContext(modalContext)
  if (!context) throw new Error("useModal needs to be used inside ModalProvider")
  return context
}

export function BudgetModalsProvider({children}: {children: React.ReactNode}) {
  const [isConfirmShown, setIsConfirmShown] = useState(false)
  const [isCancelShown, setIsCancelShown] = useState(false)
  const {
    submit,
    dispatch,
    state: {outdated, ignoredOutdated, budgets, dirty}
  } = useBudgetManagement()
  const {isBudgetAdmin} = useBudgetsPermissions()

  const changedAmount = budgets?.categories.find((cat) => cat.changes?.amount)
  const changedTag = budgets?.categories.find((cat) => cat.changes?.tag)
  const changedName = budgets?.categories.find((cat) => cat.changes?.name)
  const addedCategory = !!budgets?.newCategories?.length

  const providerContext = useMemo<Context>(
    () => ({
      showCancel: () => {
        if (dirty) setIsCancelShown(true)
        else dispatch({type: "confirmCancel"})
      },
      showConfirm: () => setIsConfirmShown(true)
    }),
    [dirty, dispatch]
  )

  return (
    <modalContext.Provider value={providerContext}>
      {children}
      <GenericModal
        noValidation
        dismissButtonText="wallet.reward.budget.confirmationModal.dismiss"
        proceedButtonText="wallet.reward.budget.confirmationModal.proceed"
        show={isConfirmShown}
        onHide={() => setIsConfirmShown(false)}
        onConfirm={() => {
          setIsConfirmShown(false)
          submit()
        }}>
        <H1 marginBottom={2}>
          <FormattedMessage id="wallet.reward.budget.confirmationModal.heading" />
        </H1>
        <IntlMarkdown id="wallet.reward.budget.confirmationModal.body" />
        <List>
          {changedAmount && (
            <ListItem
              id={
                isBudgetAdmin
                  ? "wallet.reward.budget.confirmationModal.msg.assignedAdmin"
                  : "wallet.reward.budget.confirmationModal.msg.assignedManager"
              }
            />
          )}
          {changedTag && <ListItem id="wallet.reward.budget.confirmationModal.msg.type" />}
          {changedName && <ListItem id="wallet.reward.budget.confirmationModal.msg.rename" />}
          {addedCategory && <ListItem id="wallet.reward.budget.confirmationModal.msg.newCategories" />}
        </List>
      </GenericModal>

      <GenericModal
        noValidation
        dismissButtonText="wallet.reward.budget.cancelModal.dismiss"
        proceedButtonText="wallet.reward.budget.cancelModal.proceed"
        show={isCancelShown}
        onHide={() => setIsCancelShown(false)}
        onConfirm={() => {
          setIsCancelShown(false)
          dispatch({type: "confirmCancel"})
          tracking.cancelBudgetChange()
        }}>
        <IntlMarkdown id="wallet.reward.budget.cancelModal.body" />
      </GenericModal>

      <GenericModal
        noValidation
        dismissButtonText="wallet.reward.budget.outdatedModal.dismiss"
        proceedButtonText="wallet.reward.budget.outdatedModal.proceed"
        show={outdated && !ignoredOutdated}
        onHide={() => dispatch({type: "outdatedIgnored"})}
        onConfirm={() => location.reload()}>
        <IntlMarkdown id="wallet.reward.budget.outdatedModal.body" />
      </GenericModal>
    </modalContext.Provider>
  )
}

function ListItem(props: ComponentProps<typeof IntlMarkdown>) {
  return (
    <li>
      <IntlMarkdown {...props} />
    </li>
  )
}
